import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import AppShell from "@/components/AppShell";
import { PrefsProvider, usePrefs, EmptyState, Skeleton, LogoMark } from "@/sentinel";
import Login from "@/pages/Login";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import Dashboard from "@/pages/Dashboard";
import Primary from "@/pages/Primary";
import Candidates from "@/pages/Candidates";
import Checkpoints from "@/pages/Checkpoints";
import Sources from "@/pages/Sources";
import Scoring from "@/pages/Scoring";
import FillJournal from "@/pages/FillJournal";
import IbkrReconciliation from "@/pages/IbkrReconciliation";
import History from "@/pages/History";
import Thresholds from "@/pages/Thresholds";
import Charts from "@/pages/Charts";
import Admin from "@/pages/Admin";
import Guide from "@/pages/Guide";
import { EquitiesLayout, EquitiesTape, EquitiesAdmin, EquitiesSymbol, EquitiesOpportunities, EquitiesDiscover } from "@/pages/Equities";
import { EquitiesTrackRecord, EquitiesJournal } from "@/pages/EquitiesResearch";
import EquitiesCef from "@/pages/EquitiesCef";
import EquitiesTenders from "@/pages/EquitiesTenders";
import EquitiesSpacs from "@/pages/EquitiesSpacs";
import { MegaCapLayout, MegaCapRegister, MegaCapChapters } from "@/pages/MegaCap";
import Backtest from "@/pages/Backtest";
import MobileDecision from "@/pages/MobileDecision";
import PmBoard from "@/pages/pm/Board";
import PmActivity from "@/pages/pm/Activity";
import PmDecisions from "@/pages/pm/Decisions";
import PmPrompts from "@/pages/pm/Prompts";
import PmMembers from "@/pages/pm/Members";
import PmDiscussions from "@/pages/pm/Discussions";
import PmCosts from "@/pages/pm/Costs";
import PmTimeLog from "@/pages/pm/TimeLog";
import PmContext from "@/pages/pm/Context";
import PmBudget from "@/pages/pm/Budget";
import PmMilestones from "@/pages/pm/Milestones";
import PmNotifications from "@/pages/pm/Notifications";
import PmDigest from "@/pages/pm/Digest";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="sn-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }} aria-busy="true">
        <div className="sn-stack" style={{ alignItems: "center", gap: 12, width: 200 }}>
          <LogoMark size={28} />
          <Skeleton width="100%" height={8} />
          <span className="sn-small sn-faint">Loading Sentinel…</span>
        </div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  return children;
}

function RoleGuard({ min, children }) {
  const { can, user } = useAuth();
  if (!can(min))
    return (
      <EmptyState icon="Lock" title="Access denied" data-testid="access-denied">
        This area requires the {min} role or higher. Your role is {user?.role || "viewer"}. Ask an owner if you need access.
      </EmptyState>
    );
  return children;
}

/** The Project workspace is owner-only. Other roles get the SPX shell with an access-denied state (the API refuses too). */
function PmWorkspace() {
  const { can } = useAuth();
  return can("owner") ? <AppShell workspace="pm" /> : <AppShell denied="owner" />;
}

/** Old symbol links (/equities/AAPL) collided with the section routes; symbols now live under /equities/s/. */
function LegacySymbolRedirect() {
  const { symbol } = useParams();
  return <Navigate to={`/equities/s/${encodeURIComponent(symbol || "")}`} replace />;
}

function Toasts() {
  const { theme } = usePrefs();
  return <Toaster theme={theme === "light" ? "light" : "dark"} position="bottom-right" />;
}

function App() {
  return (
    <PrefsProvider>
      <AuthProvider>
        <Toasts />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/m" element={<Protected><MobileDecision /></Protected>} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/tonight" element={<Dashboard />} />
              <Route path="/primary" element={<Primary />} />
              <Route path="/candidates" element={<Candidates />} />
              <Route path="/checkpoints" element={<Checkpoints />} />
              <Route path="/sources" element={<Sources />} />
              <Route path="/scoring" element={<Scoring />} />
              <Route path="/fills" element={<FillJournal />} />
              <Route path="/ibkr" element={<RoleGuard min="owner"><IbkrReconciliation /></RoleGuard>} />
              <Route path="/history" element={<History />} />
              <Route path="/thresholds" element={<RoleGuard min="analyst"><Thresholds /></RoleGuard>} />
              <Route path="/charts" element={<Charts />} />
              <Route path="/admin" element={<RoleGuard min="analyst"><Admin /></RoleGuard>} />
              <Route path="/backtest" element={<RoleGuard min="analyst"><Backtest /></RoleGuard>} />
              <Route path="/guide" element={<Guide />} />
              {/* The spec-author briefing was retired with the v2.2 workbook. */}
              <Route path="/briefing" element={<Navigate to="/guide" replace />} />
            </Route>
            {/* MegaCap playbook stream (roadmap Phase 7): its own workspace, separate from the 10:00 am programme. */}
            <Route path="/megacap" element={<Protected><AppShell workspace="megacap" /></Protected>}>
              <Route element={<MegaCapLayout />}>
                <Route index element={<MegaCapRegister />} />
                <Route path="chapters" element={<MegaCapChapters />} />
              </Route>
            </Route>
            {/* Equities: its own workspace, visually separate from the SPX desk. */}
            <Route path="/equities" element={<Protected><AppShell workspace="equities" /></Protected>}>
              <Route element={<EquitiesLayout />}>
                {/* Owner decision 2026-10-14: the desk opens on Fund discounts; the swing scanner is reachable at /equities/setups. */}
                <Route index element={<Navigate to="/equities/cef" replace />} />
                <Route path="setups" element={<EquitiesOpportunities />} />
                <Route path="discover" element={<EquitiesDiscover />} />
                <Route path="track-record" element={<EquitiesTrackRecord />} />
                <Route path="journal" element={<EquitiesJournal />} />
                <Route path="cef" element={<EquitiesCef />} />
                <Route path="tenders" element={<EquitiesTenders />} />
                <Route path="spacs" element={<EquitiesSpacs />} />
                <Route path="tape" element={<EquitiesTape />} />
                <Route path="watchlist" element={<EquitiesAdmin />} />
                <Route path="admin" element={<Navigate to="/equities/watchlist" replace />} />
                <Route path="s/:symbol" element={<EquitiesSymbol />} />
                <Route path=":symbol" element={<LegacySymbolRedirect />} />
              </Route>
            </Route>
            <Route path="/pm" element={<Protected><PmWorkspace /></Protected>}>
                <Route index element={<PmBoard />} />
                <Route path="activity" element={<PmActivity />} />
                <Route path="decisions" element={<PmDecisions />} />
                <Route path="discussions" element={<PmDiscussions />} />
                <Route path="costs" element={<PmCosts />} />
                <Route path="timelog" element={<PmTimeLog />} />
                <Route path="budget" element={<PmBudget />} />
                <Route path="prompts" element={<PmPrompts />} />
                <Route path="milestones" element={<PmMilestones />} />
                <Route path="notifications" element={<PmNotifications />} />
                <Route path="digest" element={<PmDigest />} />
                <Route path="context" element={<PmContext />} />
                <Route path="members" element={<PmMembers />} />
            </Route>
            <Route path="*" element={<Navigate to="/tonight" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </PrefsProvider>
  );
}

export default App;
